import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField, StandardTextFieldProps } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ja";
import "dayjs/locale/es";

// Component
import VcButton from "./DatePickerButton";
import Calendar from "./Icons/Calendar";

// Css
import "./datepicker.css";
import { getLocalStorage } from "../../../Network/ApiService";
import { constants, ls_vars } from "../../../Library/Constants";
import moment from "moment";

export interface VcDatePickerProps {
  date: Date | null;
  onDateChange: (val: Date | null) => void;
  label?: string;
  TextFieldProps?: StandardTextFieldProps;
  disableDate?: any;
  minDate?: any;
  maxDate?: any;
  format?: string;
  className?: string;
  enableTime?: boolean; // if true → use DateTimePicker
  projectTimezone?: string; // timezone to use for datetime picker
}

export default function VcDatePicker(props: VcDatePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);

  const localeFormat = getLocalStorage("i18nextLng");
  const dateFormat = getLocalStorage(ls_vars.USER_PREFERENCES)?.dateFormat?.id;
  const languagePreference =
    getLocalStorage(ls_vars.USER_PREFERENCES)?.language?.code || "en";

  // Date Format Logic
  let format = props.format || "";
  if (!props.format) {
    if (languagePreference === "ja" || languagePreference === "es") {
      format = props.enableTime ? "YYYY/MM/D(ddd) HH:mm" : "YYYY/MM/D(ddd)";
    } else if (languagePreference === "en") {
      if (dateFormat === 1) {
        format = props.enableTime ? "D, ddd MMM YYYY HH:mm" : "D, ddd MMM YYYY";
      } else if (dateFormat === 2) {
        format = props.enableTime ? "D, ddd MMM YYYY HH:mm" : "D, ddd MMM YYYY";
      } else if (dateFormat === 3) {
        format = props.enableTime ? "D, YYYY MMM ddd HH:mm" : "D, YYYY MMM ddd";
      }
    }
  }

  const SharedProps = {
    className: props.enableTime ? "calendar-picker datetime-picker" : "calendar-picker",
    disableMaskedInput: true,
    open: open,
    inputFormat: format,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    value: props.date,
    shouldDisableDate: props.disableDate,
    minDate: props.minDate,
    maxDate: props.maxDate,
    components: props.enableTime
      ? {
          OpenPickerIcon: Calendar,
          LeftArrowButton: () => null,
          RightArrowButton: () => null,
        }
      : {
          OpenPickerIcon: Calendar,
          LeftArrowButton: (btnProps: any) => (
            <VcButton
              {...btnProps}
              btnSize="xs"
              variant="text"
              text={t("Translation.previous")}
              iconStart={ArrowBack}
            />
          ),
          RightArrowButton: (btnProps: any) => (
            <VcButton
              {...btnProps}
              btnSize="xs"
              variant="text"
              text={t("Translation.next")}
              iconEnd={ArrowForward}
            />
          ),
        },
    onChange: (value: any) => {
      if (value) {
        const selectedDate = new Date(value);
        
        if (props.projectTimezone && props.enableTime) {
          // Mark this date as representing project timezone
          (selectedDate as any).isProjectTime = true;
          (selectedDate as any).projectTimezone = props.projectTimezone;
        }
        
        props.onDateChange(selectedDate);
      } else {
        props.onDateChange(null);
      }
    },
    renderInput: (params: any) => (
      <TextField
        className={
          (props.enableTime ? "calendar-picker datetime-picker " : "calendar-picker ") + (props.className ? props.className : "")
        }
        onClick={() => setOpen(true)}
        onKeyDown={(e: any) => e.preventDefault()}
        {...params}
        label={props.label}
        {...(props.TextFieldProps || {})}
        size="small"
      />
    ),
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      adapterLocale={localeFormat}
    >
      {props.enableTime ? (
        <DateTimePicker
          {...SharedProps}
          views={["year", "month", "day", "hours", "minutes"]}
        />
      ) : (
        <DatePicker
          {...SharedProps}
          views={["month", "year", "day"]}
        />
      )}
    </LocalizationProvider>
  );
}
